'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonFeature {
  name: string;
  basic: boolean | string;
  professional: boolean | string;
  enterprise: boolean | string;
}

interface ServiceComparisonProps {
  features: ComparisonFeature[];
}

export default function ServiceComparison({ features }: ServiceComparisonProps) {
  const [activeTab, setActiveTab] = useState<'all' | 'infrastructure' | 'compliance'>('all');

  const renderValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon name="CheckIcon" size={20} variant="solid" className="text-success mx-auto" />
      ) : (
        <Icon name="XMarkIcon" size={20} variant="solid" className="text-text-secondary mx-auto" />
      );
    }
    return <span className="font-body text-xs text-text-primary text-center block">{value}</span>;
  };

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden">
      <div className="border-b border-border bg-muted px-6 py-4">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-sm font-headline font-semibold text-sm transition-colors duration-smooth ${
              activeTab === 'all' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-primary'
            }`}
          >
            All Features
          </button>
          <button
            onClick={() => setActiveTab('infrastructure')}
            className={`px-4 py-2 rounded-sm font-headline font-semibold text-sm transition-colors duration-smooth ${
              activeTab === 'infrastructure' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-primary'
            }`}
          >
            Infrastructure
          </button>
          <button
            onClick={() => setActiveTab('compliance')}
            className={`px-4 py-2 rounded-sm font-headline font-semibold text-sm transition-colors duration-smooth ${
              activeTab === 'compliance' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-primary'
            }`}
          >
            Compliance
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-surface">
            <tr>
              <th className="px-6 py-4 text-left font-headline font-semibold text-sm text-primary w-1/4">
                Feature
              </th>
              <th className="px-6 py-4 text-center font-headline font-semibold text-sm text-primary w-1/4">
                Basic
              </th>
              <th className="px-6 py-4 text-center font-headline font-semibold text-sm text-primary w-1/4">
                Professional
              </th>
              <th className="px-6 py-4 text-center font-headline font-semibold text-sm text-primary w-1/4">
                Enterprise
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {features.map((feature, index) => (
              <tr key={index} className="hover:bg-muted/50 transition-colors duration-smooth">
                <td className="px-6 py-4">
                  <span className="font-body text-sm text-text-primary">{feature.name}</span>
                </td>
                <td className="px-6 py-4">{renderValue(feature.basic)}</td>
                <td className="px-6 py-4">{renderValue(feature.professional)}</td>
                <td className="px-6 py-4">{renderValue(feature.enterprise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}